/**
 * Shared vehicle data layer constants and utilities
 */

// ─── Shared Column Lists ──────────────────────────────────────────────────────

/**
 * CARD columns — only what VehicleCard renders.
 * Used by useVehicles (inventory list). Keeps the list query lean.
 * Estimated savings: ~60–70% of current 1,284 KiB payload.
 */
export const VEHICLE_CARD_COLUMNS =
  'id,make,model,trim,stock_number,year,price,mileage,body_style,fuel_type,transmission,drivetrain,exterior_color,interior_color,images,status,hot_deal,dog_hair_status,created_at';

/**
 * FULL columns — everything needed for the vehicle detail page.
 * Used by useVehicleById only.
 */
export const VEHICLE_COLUMNS =
  'id,make,model,trim,stock_number,year,price,mileage,body_style,fuel_type,transmission,drivetrain,exterior_color,interior_color,engine,vin,description,features,images,status,mpg_city,mpg_highway,honesty_score,has_carfax,service_records,owner_count,accident_free,dog_hair_status,hot_deal,reconditioning,video_url,video_duration,reconditioning_started_at,created_at';

// ─── Shared Vehicle Type ──────────────────────────────────────────────────────
export interface Vehicle {
  id: number;
  uuid: string;
  name: string;
  make: string;
  model: string;
  trim?: string;
  stock_number?: string | null;
  year: number;
  price: number;
  mileage: number;
  bodyType: string;
  fuelType: string;
  transmission: string;
  drivetrain: string;
  exteriorColor: string;
  interiorColor: string;
  engineSize: string;
  horsepower: string;
  cylinders: string;
  vin: string;
  description: string;
  story: string;
  features: string[];
  image: string;
  images: string[];
  status: 'available' | 'sold' | 'pending' | 'reconditioning';
  mpgCity?: number;
  mpgHighway?: number;
  ownerCount?: number;
  accidentHistory?: string;
  serviceRecords?: string;
  honesty_score?: number | null;
  has_carfax?: boolean | null;
  service_records_bool?: boolean | null;
  owner_count?: number | null;
  accident_free?: boolean | null;
  dog_hair_status?: string | null;
  hot_deal?: boolean | null;
  reconditioning?: any[];
  video_url?: string | null;
  video_duration?: string | null;
  reconditioning_started_at?: string | null;
  created_at?: string | null;
}

// ─── Shared Mapper ────────────────────────────────────────────────────────────
export function mapSupabaseRowToVehicle(row: any): Vehicle {
  // Generate a stable numeric ID from UUID for backward compatibility
  // Uses the first 8 hex chars of UUID to create a consistent numeric ID
  const numericId = parseInt(row.id.substring(0, 8), 16);
  
  return {
    id: numericId,
    uuid: row.id,
    name: `${row.year} ${row.make} ${row.model}`,
    make: row.make,
    model: row.model,
    trim: row.trim || '',
    stock_number: row.stock_number || null,
    year: row.year,
    price: Number(row.price),
    mileage: row.mileage,
    bodyType: row.body_style || '',
    fuelType: row.fuel_type || '',
    transmission: row.transmission || '',
    drivetrain: row.drivetrain || '',
    exteriorColor: row.exterior_color || '',
    interiorColor: row.interior_color || '',
    engineSize: row.engine || '',
    horsepower: '',
    cylinders: '',
    vin: row.vin || '',
    description: row.description || '',
    story: '',
    features: (row.features as string[]) || [],
    image: ((row.images as string[]) || [])[0] || '',
    images: (row.images as string[]) || [],
    status: (row.status as 'available' | 'sold' | 'pending' | 'reconditioning') || 'available',
    mpgCity: row.mpg_city,
    mpgHighway: row.mpg_highway,
    honesty_score: row.honesty_score ?? null,
    has_carfax: row.has_carfax ?? null,
    service_records_bool: row.service_records ?? null,
    owner_count: row.owner_count ?? null,
    accident_free: row.accident_free ?? null,
    dog_hair_status: row.dog_hair_status ?? null,
    hot_deal: row.hot_deal ?? null,
    reconditioning: row.reconditioning || [],
    video_url: row.video_url ?? null,
    video_duration: row.video_duration ?? null,
    reconditioning_started_at: row.reconditioning_started_at ?? null,
    created_at: row.created_at ?? null,
  };
}

// ─── Utility Functions ────────────────────────────────────────────────────────
export function getVehicleImage(vehicle: { image?: string; images?: string[] }): string {
  if (vehicle.images && vehicle.images.length > 0) return vehicle.images[0];
  if (vehicle.image) return vehicle.image;
  return '';
}

export function getUniqueMakes(vehicles: Vehicle[]): string[] {
  return Array.from(new Set(vehicles.map(v => v.make))).sort();
}

export function getUniqueBodyTypes(vehicles: Vehicle[]): string[] {
  return Array.from(new Set(vehicles.map(v => v.bodyType))).sort();
}

export function getUniqueFuelTypes(vehicles: Vehicle[]): string[] {
  return Array.from(new Set(vehicles.map(v => v.fuelType))).sort();
}

export function getUniqueDrivetrains(vehicles: Vehicle[]): string[] {
  return Array.from(new Set(vehicles.map(v => v.drivetrain))).sort();
}